import { useState } from 'react'
import {
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  IconButton,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react'
import { LuBell, LuCheck, LuPencil, LuPlus, LuTrash2, LuX } from 'react-icons/lu'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useCreateEventReminder } from '@/hooks/events/useCreateEventReminder'
import { useUpdateEventReminder } from '@/hooks/events/useUpdateEventReminder'
import { useDeleteEventReminder } from '@/hooks/events/useDeleteEventReminder'
import type { ApiErrorResponse } from '@/lib/api'
import type { EventDetail, EventReminder } from '@/services/events'

type Props = {
  event: EventDetail
  agendaId: string
  eventId: string
  canManageEvents: boolean
}

type ReminderFormProps = {
  agendaId: string
  eventId: string
  reminder?: EventReminder
  onCancel: () => void
}

const ReminderForm = ({ agendaId, eventId, reminder, onCancel }: ReminderFormProps) => {
  const [date, setDate] = useState(reminder?.reminder_date ?? '')
  const [note, setNote] = useState(reminder?.note ?? '')

  const createMutation = useCreateEventReminder(agendaId, eventId)
  const updateMutation = useUpdateEventReminder(agendaId, eventId, reminder?.id ?? 0)

  const mutation = reminder ? updateMutation : createMutation
  const validationErrors = (mutation.error as AxiosError<ApiErrorResponse> | null)?.response?.data?.validation_errors

  const handleCancel = () => {
    mutation.reset()
    onCancel()
  }

  const handleSubmit = (e: { preventDefault(): void }) => {
    e.preventDefault()
    const payload = { reminder_date: date, note: note.trim() || null }

    mutation.mutate(payload, {
      onSuccess: () => {
        showSuccess(reminder ? 'Lembrete atualizado com sucesso!' : 'Lembrete adicionado com sucesso!')
        onCancel()
      },
    })
  }

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap={3} p={3} borderWidth="1px" borderRadius="md" borderColor="border.subtle">
        <Flex gap={3} flexDir={{ base: 'column', sm: 'row' }}>
          <Field.Root required invalid={!!validationErrors?.reminder_date} flex="1">
            <Field.Label fontSize="sm">Data</Field.Label>
            <Input
              type="date"
              size="sm"
              variant="subtle"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
            {validationErrors?.reminder_date?.map((error, i) => (
              <Field.ErrorText key={i}>{error}</Field.ErrorText>
            ))}
          </Field.Root>

          <Field.Root invalid={!!validationErrors?.note} flex="2">
            <Field.Label fontSize="sm">Nota (opcional)</Field.Label>
            <Input
              size="sm"
              variant="subtle"
              placeholder="Ex: Confirmar presença"
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {validationErrors?.note?.map((error, i) => (
              <Field.ErrorText key={i}>{error}</Field.ErrorText>
            ))}
          </Field.Root>
        </Flex>

        <Flex gap={2} justify="flex-end">
          <Button size="xs" variant="outline" onClick={handleCancel} disabled={mutation.isPending}>
            <LuX />
            Cancelar
          </Button>
          <Button size="xs" type="submit" colorPalette="primary" loading={mutation.isPending}>
            <LuCheck />
            {reminder ? 'Salvar' : 'Adicionar'}
          </Button>
        </Flex>
      </Stack>
    </form>
  )
}

type ReminderRowProps = {
  reminder: EventReminder
  agendaId: string
  eventId: string
  canManageEvents: boolean
}

const ReminderRow = ({ reminder, agendaId, eventId, canManageEvents }: ReminderRowProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const deleteMutation = useDeleteEventReminder(agendaId, eventId)

  const handleDelete = () => {
    deleteMutation.mutate(reminder.id, {
      onSuccess: () => showSuccess('Lembrete removido com sucesso!'),
    })
  }

  if (isEditing) {
    return (
      <ReminderForm
        agendaId={agendaId}
        eventId={eventId}
        reminder={reminder}
        onCancel={() => setIsEditing(false)}
      />
    )
  }

  return (
    <Flex
      align="center"
      justify="space-between"
      gap={3}
      py={2}
      px={3}
      borderWidth="1px"
      borderRadius="md"
      borderColor="border.subtle"
    >
      <Box>
        <Text fontSize="sm" fontWeight="medium">
          {format(new Date(reminder.reminder_date + 'T00:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
        </Text>
        {reminder.note && (
          <Text fontSize="xs" color="fg.muted" mt={0.5}>{reminder.note}</Text>
        )}
      </Box>

      {canManageEvents && (
        <Flex gap={1} flexShrink={0}>
          <IconButton
            size="xs"
            variant="ghost"
            colorPalette="yellow"
            color="yellow.600"
            aria-label="Editar lembrete"
            onClick={() => setIsEditing(true)}
            disabled={deleteMutation.isPending}
          >
            <LuPencil />
          </IconButton>
          <IconButton
            size="xs"
            variant="ghost"
            colorPalette="red"
            color="red.600"
            aria-label="Remover lembrete"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            <LuTrash2 />
          </IconButton>
        </Flex>
      )}
    </Flex>
  )
}

const RemindersSection = ({ event, agendaId, eventId, canManageEvents }: Props) => {
  const [isAdding, setIsAdding] = useState(false)

  return (
    <Card.Root>
      <Card.Body>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <Flex align="center" gap={3}>
            <Box
              bg="orange.subtle"
              color="orange.600"
              p={2}
              borderRadius="lg"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <LuBell size={16} />
            </Box>
            <Box>
              <Heading size="sm" fontWeight="semibold">Lembretes</Heading>
              <Text fontSize="xs" color="fg.muted">Datas de acompanhamento do evento</Text>
            </Box>
          </Flex>

          {canManageEvents && !isAdding && (
            <Button size="xs" variant="ghost" onClick={() => setIsAdding(true)} color="primary.500" fontWeight={600}>
              <LuPlus />
              Adicionar
            </Button>
          )}
        </Flex>

        <Stack gap={2}>
          {event.reminders.map((reminder) => (
            <ReminderRow
              key={reminder.id}
              reminder={reminder}
              agendaId={agendaId}
              eventId={eventId}
              canManageEvents={canManageEvents}
            />
          ))}

          {event.reminders.length === 0 && !isAdding && (
            <Text fontSize="sm" color="fg.muted">Nenhum lembrete adicionado a este evento.</Text>
          )}

          {isAdding && (
            <ReminderForm
              agendaId={agendaId}
              eventId={eventId}
              onCancel={() => setIsAdding(false)}
            />
          )}
        </Stack>
      </Card.Body>
    </Card.Root>
  )
}

export default RemindersSection
